// libs
import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import ContentEntityEditor from '@/components/ContentEditor/ContentEntityEditor';
import { useAppConfig } from '@/hooks/useAppConfig';
import ContentEditorSettings from '@/modules/corporate/components/ContentEditorSettings';
import { useEmbeddedExerciseEditor } from '@/modules/corporate/modules/course/components/ExerciseCardContainer/components/ExerciseEditorModal/context';
import {
  ExerciseType,
  ResourceStatusEnum,
  ResourceTypeEnum,
  WorkspaceSubjects,
} from '@/types/schema';

import { getFormFields, getSectionLayout, validateForm } from './config';
import { EditorProps } from './types';

function FormContainer({
  data: { exercise, initialValues },
  methods: { onPublish, onRevertSuccess, onSaveDraft, onSaveDraftAndPreview, onSubmit, setIsDirty },
  state: { isDirty, isDuplicating, isEditMode, disabled, formKey },
  formRef,
}: EditorProps) {
  const { t } = useTranslation();
  const { site } = useAppConfig();
  const embeddedEditor = useEmbeddedExerciseEditor();
  const { subWorkspacesEnabled, enableAssignmentRestriction } = site;
  const status = exercise?.status;

  const formFields = useMemo(
    () =>
      getFormFields(t, {
        isEditMode: isEditMode && exercise?.status !== ResourceStatusEnum.Draft,
        formInitialValues: initialValues,
      }),
    [t, isEditMode, exercise?.status, initialValues],
  );

  const sectionLayout = useMemo(
    () => getSectionLayout({ t, subWorkspacesEnabled, enableAssignmentRestriction }),
    [t, subWorkspacesEnabled, enableAssignmentRestriction],
  );

  const onChange = useCallback(
    (response: any) => {
      setIsDirty(response.dirty);
    },
    [setIsDirty],
  );

  const formTitle = isEditMode
    ? t('Exercise:Creator.EditExercise')
    : t('Exercise:Creator.NewExercise');

  const settings =
    exercise && !isDuplicating && !embeddedEditor ? (
      <ContentEditorSettings
        content={exercise as ExerciseType}
        itemHeader={t('Common:ResourceTypeSM.Exercise')}
        onRevertSuccess={onRevertSuccess}
      />
    ) : undefined;

  return (
    <ContentEntityEditor
      key={formKey}
      form={{
        title: isEditMode ? t('Exercise:Creator.EditExercise') : t('Exercise:Creator.NewExercise'),
        description: t('Exercise:Creator.NewExerciseDescription'),
        fields: formFields,
        formName: 'ExerciseEditor',
        initialValues: initialValues,
        sectionLayout,
        validate: validateForm(t, subWorkspacesEnabled),
        onChange,
        onSubmit,
        formRef,
      }}
      header={{
        disabled,
        isArchived: status === ResourceStatusEnum.Archived,
        isEditMode,
        title: formTitle,
        status: isDuplicating ? undefined : status,
        settings,
        workspaceSubject: WorkspaceSubjects.Resource,
        contentType: ResourceTypeEnum.Exercise,
        id: exercise?.id,
      }}
      isDirty={isDirty}
      onBack={embeddedEditor?.onClose}
      onPublish={onPublish}
      onSaveDraft={onSaveDraft}
      onSaveDraftAndPreview={onSaveDraftAndPreview}
    />
  );
}

export default React.memo(FormContainer);
